import React, { useState } from 'react';
import { Modal } from '@mantine/core';
import { formatTenbisDesiredTime, resolveTenbisPhase, takeAwayChannelLabel, type OrderView, type TenbisStatusAction } from 'shared-models/browser';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import { useTenbisOrderActions } from './useTenbisOrderActions';
import {
  tenbisActionLabel,
  tenbisDeliveryLabel,
  tenbisPaymentLabel,
  tenbisShortId,
  tenbisStatusLabel,
  visibleTenbisActions,
} from './tenbis-order.utils';
import classes from './tenbis-order-panel.module.scss';

export type TenbisOrderPanelProps = {
  tenantId: string;
  employeeSession: string | null;
  order: OrderView;
  // Accept succeeded: the parent prints this device's bon (+ receipt when closed).
  onAccepted?: (order: OrderView) => void;
};

// Channel card for an aggregator order: header, meta line, customer block, total, lifecycle button.
export const TenbisOrderPanel: React.FC<TenbisOrderPanelProps> = ({ tenantId, employeeSession, order, onAccepted }) => {
  const { inFlight, apply } = useTenbisOrderActions({ tenantId, employeeSession, orderId: order.id });
  const [pendingAction, setPendingAction] = useState<TenbisStatusAction | null>(null);

  if (!order.tenbisOrderId) {
    return null;
  }

  const channelLabel = takeAwayChannelLabel(order.orderCode);
  const actions = visibleTenbisActions(order.tenbisStatus);
  const desiredTime = formatTenbisDesiredTime(order.tenbisDesiredTime);
  const paymentLabel = tenbisPaymentLabel(order.tenbisPaymentMethod);
  const isBusy = inFlight !== null;
  const phase = resolveTenbisPhase(order.tenbisStatus);
  const isVoidedButOpen = (phase === 'canceled' || phase === 'handled_by_tenbis') && order.status === 'open';
  const pendingLabel = pendingAction ? tenbisActionLabel(pendingAction, order.tenbisDeliveryMethod) : '';

  return (
    <div className={classes.panel} data-testid="tenbis-order-panel">
      <div className={classes.header}>
        <span className={classes.brand}>{channelLabel}</span>
        <span className={classes.number} data-testid="tenbis-order-number">#{tenbisShortId(order.tenbisOrderId)}</span>
        <span className={classes.badge} data-testid="tenbis-order-status">{tenbisStatusLabel(order.tenbisStatus)}</span>
      </div>
      <div className={classes.meta}>
        {desiredTime && (
          <span className={classes.time} data-testid="tenbis-order-desired-time">מועד: {desiredTime}</span>
        )}
        <span className={classes.chip} data-testid="tenbis-order-delivery">{tenbisDeliveryLabel(order.tenbisDeliveryMethod)}</span>
        {paymentLabel && (
          <span className={classes.chip} data-testid="tenbis-order-payment">{paymentLabel}</span>
        )}
      </div>
      {(order.guestName || order.tenbisNote) && (
        <div className={classes.customer} data-testid="tenbis-order-customer">
          {order.guestName && (
            <p className={classes.guest} data-testid="tenbis-order-guest">{order.guestName}</p>
          )}
          {order.tenbisNote && (
            <p className={classes.note} data-testid="tenbis-order-note">{order.tenbisNote}</p>
          )}
        </div>
      )}
      <div className={classes.total} data-testid="tenbis-order-total">
        <span>סה"כ</span>
        <span>{formatMoney(Number(order.totalAmount ?? 0))}</span>
      </div>
      {isVoidedButOpen && (
        <p className={classes.hint} data-testid="tenbis-order-voided-hint">ההזמנה בוטלה ב{channelLabel} — יש לבטל את הפריטים ידנית</p>
      )}
      {actions.length > 0 && (
        <div className={classes.actions}>
          {actions.includes('accept') && (
            <Button variant="save" fullWidth onClick={() => setPendingAction('accept')} disabled={isBusy} loading={inFlight === 'accept'} dataTestId="tenbis-order-accept">
              קבל
            </Button>
          )}
          {actions.includes('out_for_delivery') && (
            <Button
              variant="confirm"
              fullWidth
              onClick={() => setPendingAction('out_for_delivery')}
              disabled={isBusy}
              loading={inFlight === 'out_for_delivery'}
              dataTestId="tenbis-order-out-for-delivery"
            >
              {tenbisActionLabel('out_for_delivery', order.tenbisDeliveryMethod)}
            </Button>
          )}
        </div>
      )}
      <Modal
        opened={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        title={`${pendingLabel} — הזמנת ${channelLabel}`}
        centered
        radius="md"
        zIndex={302}
        closeButtonProps={{ 'data-testid': 'tenbis-order-confirm-close' } as Record<string, string>}
      >
        <div className={classes.modalBody} data-testid="tenbis-order-confirm-modal">
          <p className={classes.modalText}>
            {pendingAction === 'accept'
              ? `ההזמנה תישלח למטבח ותעודכן ב${channelLabel} כ"בהכנה".`
              : `ההזמנה תעודכן ב${channelLabel} כיצאה מהמסעדה.`}
          </p>
          <div className={classes.modalActions}>
            <Button variant="cancel" fullWidth onClick={() => setPendingAction(null)} disabled={isBusy} dataTestId="tenbis-order-confirm-cancel">
              ביטול
            </Button>
            <Button
              variant="confirm"
              fullWidth
              loading={isBusy}
              onClick={async () => {
                if (!pendingAction) {
                  return;
                }
                const updated = await apply(pendingAction);
                if (!updated) {
                  return;
                }
                setPendingAction(null);
                if (pendingAction === 'accept') {
                  onAccepted?.(updated);
                }
              }}
              dataTestId="tenbis-order-confirm-submit"
            >
              {pendingLabel}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
